/** Indian Standard Time helpers for Admin (Supabase timestamps are UTC). */
export const APP_TIME_ZONE = 'Asia/Kolkata';

/**
 * Supabase/Postgres often returns UTC without a Z suffix.
 * Treat naive strings as UTC so IST conversion is correct.
 */
export function parseUtcTimestamp(
  value: string | number | Date | null | undefined,
): Date | null {
  if (value == null || value === '') return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(trimmed)) {
    const d = new Date(trimmed);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const normalized = trimmed.includes('T')
    ? trimmed
    : trimmed.replace(' ', 'T');
  const d = new Date(`${normalized}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

const FORMAT_PRESETS: Record<string, Intl.DateTimeFormatOptions> = {
  short: {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  },
  medium: {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  },
  long: {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  },
  full: {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  },
  shortDate: {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  },
  mediumDate: {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  },
  longDate: {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  },
  fullDate: {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  },
  shortTime: {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  },
  mediumTime: {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  },
};

function optionsFromAngularFormat(
  format: string,
): Intl.DateTimeFormatOptions {
  if (FORMAT_PRESETS[format]) {
    return FORMAT_PRESETS[format];
  }

  // Lightweight support for patterns like "MMM d, y, h:mm a"
  const opts: Intl.DateTimeFormatOptions = {};
  if (/y/.test(format)) opts.year = 'numeric';
  if (/M{3,}/.test(format)) opts.month = 'short';
  else if (/M/.test(format)) opts.month = 'numeric';
  if (/d/.test(format)) opts.day = 'numeric';
  if (/H|h/.test(format)) {
    opts.hour = 'numeric';
    opts.hour12 = /a/i.test(format) || !/H/.test(format);
  }
  if (/m/.test(format)) opts.minute = '2-digit';
  if (/s/.test(format)) opts.second = '2-digit';
  if (/E{3,}|EEEE/.test(format)) opts.weekday = 'long';

  return Object.keys(opts).length
    ? opts
    : FORMAT_PRESETS['medium'];
}

export function formatIstDate(
  value: string | number | Date | null | undefined,
  format = 'medium',
  fallback = '-',
): string {
  const date = parseUtcTimestamp(value);
  if (!date) return fallback;

  try {
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: APP_TIME_ZONE,
      ...optionsFromAngularFormat(format),
    }).format(date);
  } catch {
    return fallback;
  }
}
